import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { SHOP_SERVICE_TYPE } from 'global_constants';
import { getGooglePlaceValue, isEmpty } from '_dash';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const getDifferenceInHours = (startTime) => {
  const bookingStartTime = dayjs.utc(startTime);
  const currentDateTime = dayjs.utc();
  return currentDateTime.diff(bookingStartTime, 'hour') * -1;
};

export const getBookingStart = (booking) => {
  return dayjs(booking.start).utc(false).format('hh:mm A (dddd, DD MMMM)');
};

export const getBookingServices = (booking) => {
  return (booking.booked_services || []).map((item) => item.name).join(', ');
};

export const getBookingNote = (barber, booking) => {
  return `${barber.display_name}: " ${booking.barber_note} "`;
};

export const getBookingAddress = (booking) => {
  return booking.location_type === SHOP_SERVICE_TYPE ? booking.barber?.shop_address?.formatted_name || 'shop' : booking?.booking_address?.address_text || 'N/A';
};

export const getScheduleItem = (schedule) => {
  return `${dayjs(schedule.from_time, 'hh:mm:ss').format('h:mm A')} - ${dayjs(schedule.to_time, 'hh:mm:ss').format('h:mm A')}`;
};

export const getMapLink = (address) => {
  return `https://www.google.com/maps/?q=${address}`;
};

export const openMapLink = (address, coordinates) => {
  // Enhanced version with coordinate support for better accuracy
  if (coordinates && coordinates.latitude && coordinates.longitude) {
    // Import openMapsApp dynamically to avoid circular dependency
    import('services/mapboxService').then(({ openMapsApp }) => {
      openMapsApp(coordinates.latitude, coordinates.longitude, address);
    }).catch(() => {
      // Fallback to original behavior if mapbox service fails
      window.open(getMapLink(address), '_blank');
    });
  } else {
    // Original behavior - address-based link
    return window.open(getMapLink(address), '_blank');
  }
};

export const setMetaDescription = (content) => {
  const meta = document.getElementsByTagName('meta');
  for (var i = 0; i < meta.length; i++) {
    if (meta[i].name.toLowerCase() === 'description') {
      meta[i].content = content;
    }
  }
};

export const setMetaTitle = (title) => {
  document.title = title;
};

export const setMetaImage = (image) => {
  // Update or create Open Graph image meta tag
  let ogImageTag = document.querySelector('meta[property="og:image"]');
  if (!ogImageTag) {
    ogImageTag = document.createElement('meta');
    ogImageTag.setAttribute('property', 'og:image');
    document.head.appendChild(ogImageTag);
  }
  ogImageTag.setAttribute('content', image);

  // Update or create Twitter Card image meta tag
  let twitterImageTag = document.querySelector('meta[name="twitter:image"]');
  if (!twitterImageTag) {
    twitterImageTag = document.createElement('meta');
    twitterImageTag.setAttribute('name', 'twitter:image');
    document.head.appendChild(twitterImageTag);
  }
  twitterImageTag.setAttribute('content', image);
};

export const setMetaOgTitle = (title) => {
  let ogTitleTag = document.querySelector('meta[property="og:title"]');
  if (!ogTitleTag) {
    ogTitleTag = document.createElement('meta');
    ogTitleTag.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitleTag);
  }
  ogTitleTag.setAttribute('content', title);
};

export const setMetaOgDescription = (description) => {
  let ogDescriptionTag = document.querySelector('meta[property="og:description"]');
  if (!ogDescriptionTag) {
    ogDescriptionTag = document.createElement('meta');
    ogDescriptionTag.setAttribute('property', 'og:description');
    document.head.appendChild(ogDescriptionTag);
  }
  ogDescriptionTag.setAttribute('content', description);
};

export const getCalloutDisplayAddress = (place) => {
  let displayName = '';
  if (!isEmpty(place)) {
    const postalCode = getGooglePlaceValue(place, ['postal_code']);
    const city = getGooglePlaceValue(place, ['locality', 'postal_town']);
    displayName = [postalCode, city].filter((i) => i).join(', ');
  }
  return displayName;
};

/**
 * Opens Intercom by simulating a click on a div with the given class name.
 * Defaults to 'intercom-provider-div'.
 * @param {string} className - The class name of the div to click.
 */
export const openIntercom = (className = 'intercom-provider-div') => {
  const div = document.querySelector(`.${className}`);
  if (div) {
    div.click();
  } else if (window.Intercom) {
    // fallback: open Intercom directly if available
    // update interco prope
    window.Intercom('show');
  }
};
